import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, Sparkles, Users, ArrowRight, Check } from 'lucide-react';
import auraLogo from '@/assets/aura-logo.png';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onClose: () => void;
}

const steps = [
  {
    id: 'capture',
    title: 'Capture',
    subtitle: 'Build Your Archive',
    description: 'Snap photos of your clothing. Our AI instantly identifies brand, fabric, color, and style—transforming your physical wardrobe into a digital asset.',
    icon: Camera,
    color: 'from-blue-500/20 to-indigo-500/20',
    tips: ['Natural lighting works best', 'Flat lay or hanger shots', 'One item per photo'],
  },
  {
    id: 'curate',
    title: 'Curate',
    subtitle: 'Generate Perfect Looks',
    description: 'Tell us the occasion, mood, or weather. The Gemini engine analyzes your archive and creates outfit combinations you never imagined.',
    icon: Sparkles,
    color: 'from-amber-500/20 to-orange-500/20',
    tips: ['Set your style preferences', 'Save looks to your calendar', 'Earn Aura with every generation'],
  },
  {
    id: 'connect',
    title: 'Connect',
    subtitle: 'Join The Syndicate',
    description: 'Share your fits with a global community of tastemakers. Get inspired, give appreciation, and watch your Aura grow.',
    icon: Users,
    color: 'from-purple-500/20 to-pink-500/20',
    tips: ['Share to Instagram Stories', 'Follow style icons', 'Rise to Icon status'],
  },
];

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 bg-card border border-border overflow-hidden">
        {/* Logo Header */}
        <div className="flex items-center justify-center gap-2 pt-6 pb-2">
          <img src={auraLogo} alt="AURA" className="w-8 h-8" />
          <span className="text-sm tracking-[0.3em] font-light text-foreground">AURA</span>
        </div>
        
        {/* Progress Bar */}
        <div className="flex gap-1 px-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                i <= currentStep ? 'bg-accent' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div 
          className={`px-8 pb-8 transition-all duration-300 ${
            isAnimating ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
          }`}
        >
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center`}>
            <Icon size={32} className="text-foreground" />
          </div>

          {/* Step Number */}
          <p className="text-[10px] tracking-[0.4em] uppercase text-center text-muted-foreground mb-2">
            Step {currentStep + 1} of {steps.length}
          </p>

          {/* Title */}
          <h2 className="font-serif text-4xl md:text-5xl text-center text-foreground mb-2">
            {step.title}
          </h2>
          <p className="text-center text-accent text-sm tracking-wide mb-6">
            {step.subtitle}
          </p>

          {/* Description */}
          <p className="text-center text-muted-foreground leading-relaxed mb-8">
            {step.description}
          </p>

          {/* Tips */}
          <div className="space-y-3 mb-10">
            {step.tips.map((tip, i) => (
              <div 
                key={i}
                className="flex items-center gap-3 text-sm text-muted-foreground"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                  <Check size={12} className="text-accent" />
                </div>
                {tip}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip Tutorial
            </button>
            <button
              onClick={handleNext}
              className="group flex items-center gap-3 bg-foreground text-background px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Get Started' : 'Continue'}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-accent/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />
      </DialogContent>
    </Dialog>
  );
};
